import { useEffect } from "react";

import { getErrorMessage } from "~/v2/api/hooks";
import type { PositionsProduct } from "~/v3-poc/api/positions";
import { LiquidateAllPositions } from "./LiquidateAllPositions.connector";
import { useRiskControlsDialogController } from "./RiskControlsDialog.controller";
import { RiskControlsDialogPresentation } from "./RiskControlsDialog.pres";
import type { RiskControlSelection } from "./riskControls.model";
import { useLiquidateAllPositions } from "./useLiquidateAllPositions";
import { useSetRiskControl } from "./useRiskControls";

export interface RiskControlsDialogProps {
  accountID?: string;
  product?: PositionsProduct;
  open: boolean;
  current: RiskControlSelection;
  isLoading: boolean;
  loadErrorMessage?: string;
  onOpenChange: (open: boolean) => void;
}

export const RiskControlsDialog = ({
  accountID,
  product,
  open,
  current,
  isLoading,
  loadErrorMessage,
  onOpenChange,
}: RiskControlsDialogProps) => {
  const { mutate, isPending, error: submitError, reset } = useSetRiskControl();

  const liquidation = useLiquidateAllPositions({
    accountID,
    product,
    enabled: open,
  });

  const controller = useRiskControlsDialogController({
    current,
    open,
    onApply: (change) => {
      if (!accountID) {
        return;
      }
      mutate(
        {
          accountID,
          control: change.control,
          enabled: change.enabled,
        },
        {
          onSuccess: () => onOpenChange(false),
        },
      );
    },
    // Entering or leaving the confirmation drops a stale result or error.
    onViewChange: () => liquidation.reset(),
  });

  const resetLiquidation = liquidation.reset;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `open` is a trigger — closing and reopening both clear stale mutation success/error so the next opening paint cannot flash a previous result. The body writes rather than reads it.
  useEffect(() => {
    reset();
    resetLiquidation();
  }, [open, reset, resetLiquidation]);

  return (
    <RiskControlsDialogPresentation
      open={open}
      onOpenChange={(next) => {
        if (!isPending && !liquidation.isPending) {
          onOpenChange(next);
        }
      }}
      mode={controller.mode}
      view={controller.view}
      activeControl={controller.activeControl}
      selection={controller.selection}
      canApply={controller.canApply}
      isLoading={isLoading}
      isPending={isPending}
      loadErrorMessage={loadErrorMessage}
      submitErrorMessage={
        submitError ? getErrorMessage(submitError) : undefined
      }
      onControlChange={controller.setControlChecked}
      onSubmit={controller.onSubmit}
      onConfirmLift={controller.onConfirmLift}
      liquidateAll={
        <LiquidateAllPositions
          active={controller.view === "liquidate"}
          positionCount={liquidation.positionCount}
          liquidatedCount={liquidation.liquidatedCount}
          isLoadingPositions={liquidation.isLoadingPositions}
          positionsErrorMessage={liquidation.positionsErrorMessage}
          isPending={liquidation.isPending}
          isSuccess={liquidation.isSuccess}
          errorMessage={liquidation.errorMessage}
          onRetryPositions={liquidation.onRetryPositions}
          liquidate={liquidation.liquidate}
          disabled={isPending}
          onRequestLiquidate={() => controller.setView("liquidate")}
          onBack={() => controller.setView("controls")}
        />
      }
    />
  );
};
